'use client';

import { useContext, useMemo } from 'react';
import { useTranslation } from '../../../hooks/use-translation';
import { kImageFolderUrl } from '../../../services/constants';
import SpriteIcon from '../../shared/sprite-icon';
import { useBuildDownloadUrl } from '../../../hooks/use-download-url-builder';
import GlobalContext from '../../../context/global-context';
import OWLink from '../../shared/ow-link';
import useIsIframe from '../../../hooks/use-is-iframe';

// -----------------------------------------------------------------------------
export const PageFooter = (): JSX.Element | null => {
  const { t } = useTranslation();
  const isIframe = useIsIframe();

  const { config } = useContext(GlobalContext);
  const {
    baseUrl,
    curesforgeBase,
    tebexBase,
    nitroBase,
    owIdeaBase,
    supportBaseUrl,
    cfStudiosBase } = config?.publicRuntimeConfig?.base;

  const {
    discord,
    reddit,
    facebook,
    linkedin,
    twitter } = config?.publicRuntimeConfig?.links.social;

  const { footerLinks, appstore } = config?.publicRuntimeConfig?.links;

  const downloadUrl = useBuildDownloadUrl({
    channel: 'web_dl_btn',
    utmSource: 'web_app_store',
  });

  const fullYear = useMemo<string>(() => {
    return `${new Date().getFullYear()}`;
  }, []);

  const handleClickBackToTop = (): void => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Hidden inside the embedded Overwolf iframe flow — the marketing footer/links
  // are chrome the in-client store doesn't want. Server-rendered for normal web
  // (preserves SEO); only removed client-side once we know we're in an iframe.
  if (isIframe) {
    return null;
  }

  return (
    <footer className='site-footer'>
      <button
        className='back-to-top'
        onClick={handleClickBackToTop}
      >
        <SpriteIcon icon='big-arrow' />
        {t('backToTop')}
      </button>
      <img
        src={`${kImageFolderUrl}/footer-shield.webp`}
        width='261px'
        height='303px'
        className='ow-shield'
        alt='Overwolf'
      />

      <div className='site-footer-nav'>
        <SpriteIcon icon='ow-symbol' svgClassName='ow-symbol' />
        <SpriteIcon icon='overwolf-logo-and-text' svgClassName='ow-logo' />
        <nav>
          <ul>
            <li>
              <h5>{t('footer.creators')}</h5>
              <ul>
                <li>
                  <OWLink href={`${footerLinks.buildAnApp}`}>
                    {t('footer.buildApp')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${curesforgeBase}`}>
                    {t('footer.publishMod')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${tebexBase}`}>
                    {t('footer.monetizeServer')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${nitroBase}`}>
                    {t('footer.monetizeWebsite')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.gettingStarted}`}
                  >
                    {t('footer.appDoc')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.modDocs}`}
                  >
                    {t('footer.modDoc')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.acceleration}`}
                  >
                    {t('footer.accelerator')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.fund}`}
                  >
                    {t('footer.applyFunding')}
                  </OWLink>
                </li>
              </ul>
            </li>
            <li>
              <h5>{t('footer.gamers')}</h5>
              <ul>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${appstore}`}
                  >
                    {t('footer.appstore')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${curesforgeBase}`}
                  >
                    {t('footer.mods')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={downloadUrl}
                  >
                    {t('footer.downloadOverwolf')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${owIdeaBase}`}
                  >
                    {t('footer.ideas')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${supportBaseUrl}`}
                  >
                    {t('footer.helpCenter')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${discord}`}
                  >
                    {t('footer.discordCommunity')}
                  </OWLink>
                </li>
              </ul>
            </li>
            <li>
              <h5>{t('footer.partners')}</h5>
              <ul>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${cfStudiosBase}`}
                  >
                    {t('footer.gameStudios')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${footerLinks.ads}`}>
                    {t('footer.advertise')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${footerLinks.adsAudience}`}>
                    {t('footer.audiences')}
                  </OWLink>
                </li>
                <li>
                  <OWLink href={`${footerLinks.adsSolutions}`}>
                    {t('footer.adSolutions')}
                  </OWLink>
                </li>
              </ul>
            </li>
            <li>
              <h5>{t('footer.company')}</h5>
              <ul>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.about}`}
                  >
                    {t('footer.aboutUs')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.culture}`}
                  >
                    {t('footer.ourCulture')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${baseUrl}/brand-guidelines/`}
                  >
                    {t('footer.brandGuidelines')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={footerLinks.blog}
                  >
                    {t('footer.blog')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.careers}`}
                  >
                    {t('footer.careers')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.contactUs}`}
                  >
                    {t('footer.contactUs')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.iso}`}
                  >
                    {t('footer.iso')}
                  </OWLink>
                </li>
              </ul>
            </li>
            <li>
              <h5>{t('footer.support')}</h5>
              <ul>
                <li>
                  <OWLink
                    className='list-item' href={`${supportBaseUrl}`}
                  >
                    {t('footer.helpCenter')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.supportedGames}`}
                  >
                    {t('supportedGames')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.terms}`}
                  >
                    {t('footer.terms')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.privacy}`}
                  >
                    {t('footer.privacy')}
                  </OWLink>
                </li>
                <li>
                  <OWLink
                    className='list-item'
                    href={`${footerLinks.licenses}`}
                  >
                    {t('footer.licenses')}
                  </OWLink>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>

      <div className='site-footer-social'>
        <div className='site-footer-social-inner'>
          <span className='copyright'>
            {t('footer.trademark', { fullYear })}
          </span>
          <ul className='social-links'>
            <li>
              <OWLink
                href={`${discord}`}
                title={t('footer.discord')}
                target='_blank'
              >
                <SpriteIcon icon='Discord' />
              </OWLink>
            </li>
            <li>
              <OWLink
                href={`${reddit}`}
                title={t('footer.reddit')}
                target='_blank'
              >
                <SpriteIcon icon='Reddit' />
              </OWLink>
            </li>
            <li>
              <OWLink
                href={`${facebook}`}
                title={t('footer.facebook')}
                target='_blank'
              >
                <SpriteIcon icon='Facebook' />
              </OWLink>
            </li>
            <li>
              <OWLink
                href={`${linkedin}`}
                title={t('footer.linkedIn')}
                target='_blank'
              >
                <SpriteIcon icon='LinkedIn' />
              </OWLink>
            </li>
            <li>
              <OWLink
                href={`${twitter}`}
                title={t('footer.twitter')}
                target='_blank'
              >
                <SpriteIcon icon='Twitter' />
              </OWLink>
            </li>
          </ul>
        </div>

      </div>
    </footer>
  );
};
